import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";

const DS = {
  border: "rgba(0,0,0,0.08)",
};

export default function HowToGuideBox({
  title,
  steps = [],
  tips = [],
  role = "Instructor & Course Coordinators",
  iconOnly = false,
}) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimary10 = alpha(tPrimary, 0.06);
  const tSecondary = theme.palette.secondary.main;
  const [open, setOpen] = useState(false);

  return (
    <>
      {iconOnly ? (
        <IconButton
          size="small"
          onClick={() => setOpen(true)}
          sx={{ color: tPrimary, opacity: 0.7, "&:hover": { opacity: 1, bgcolor: tPrimary10 } }}
          aria-label="Help"
        >
          <HelpOutlineIcon sx={{ fontSize: 18 }} />
        </IconButton>
      ) : (
        <Button
          size="small"
          variant="outlined"
          startIcon={<HelpOutlineIcon />}
          onClick={() => setOpen(true)}
          sx={{
            borderColor: tPrimary,
            color: tPrimary,
            fontSize: "0.8rem",
            textTransform: "none",
            "&:hover": { backgroundColor: tPrimary10 },
          }}
        >
          Help
        </Button>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle
          sx={{
            backgroundColor: tPrimary10,
            borderBottom: `1px solid ${DS.border}`,
            pr: 6,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} color={tPrimary} sx={{ fontFamily: "var(--font-display)" }}>
            {title}
          </Typography>
          <Chip
            size="small"
            label={`For: ${role}`}
            variant="outlined"
            sx={{ mt: 0.5, height: 22, fontSize: "0.72rem" }}
          />
          <IconButton
            onClick={() => setOpen(false)}
            size="small"
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {steps.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="overline"
                sx={{ color: tPrimary, fontWeight: 700, letterSpacing: "0.08em" }}
              >
                Step-by-Step Guide
              </Typography>
              {steps.map((step, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    gap: 2,
                    mt: 2,
                    pb: 2,
                    borderBottom:
                      i < steps.length - 1 ? `1px solid ${DS.border}` : "none",
                  }}
                >
                  <Box
                    sx={{
                      minWidth: 28,
                      height: 28,
                      borderRadius: "50%",
                      backgroundColor: tPrimary,
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {step.number}
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      color={tPrimary}
                      sx={{ mb: 0.3, fontFamily: "var(--font-display)" }}
                    >
                      {step.title}
                    </Typography>
                    {step.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {step.description}
                      </Typography>
                    )}
                    {step.details && (
                      <Box
                        component="ul"
                        sx={{
                          m: 0,
                          pl: 2,
                          fontSize: "0.82rem",
                          color: "text.secondary",
                          "& li": { mb: 0.3 },
                        }}
                      >
                        {Array.isArray(step.details) ? (
                          step.details.map((d, j) => <li key={j}>{d}</li>)
                        ) : (
                          <li>{step.details}</li>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {tips.length > 0 && (
            <Box
              sx={{
                borderLeft: `3px solid ${tSecondary}`,
                pl: 2,
                py: 0.5,
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: tSecondary,
                  fontWeight: 700,
                  letterSpacing: "0.10em",
                  display: "block",
                  mb: 1,
                }}
              >
                Best Practices
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none" }}>
                {tips.map((tip, i) => (
                  <Box
                    key={i}
                    component="li"
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      alignItems: "flex-start",
                      mb: i < tips.length - 1 ? 1 : 0,
                    }}
                  >
                    <Box
                      sx={{
                        mt: "6px",
                        width: 5,
                        height: 5,
                        borderRadius: "50%",
                        bgcolor: tSecondary,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                      {tip}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
